'use client';

import { c as _c } from "react/compiler-runtime";
import { type CustomerReportPreviewWithShipments, type CustomerReportSendResult, previewCustomerReportNightlyByDate, runCustomerReportNightlyByDate, sendCustomerReportSummaryByDate } from '@/app/_workflows/sendCustomerReportNightly';
import type { CustomerReportIssueReason } from '@shared/EmailService';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { DatePicker } from '@shared/components/DatePicker';
import { Modal } from '@shared/components/Modal';
import { useServerHandler } from '@shared/hooks/useServerHandler';
import type dayjs from 'dayjs';
import { useEffect, useState } from 'react';
function formatDateAsLocalDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
function formatDateForDisplay(date: Date): string {
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(date.getUTCDate()).padStart(2, '0');
  return `${mm}/${dd}/${date.getUTCFullYear()}`;
}
const CUSTOMER_REPORT_ISSUE_LABELS: Record<CustomerReportIssueReason, string> = {
  PLANNING_NOT_REQUIRED: 'Planning not required',
  NO_SHEET: 'No sheet',
  SHEET_NOT_COMPLETED: 'Sheet not completed',
  MISSING_COC: 'Missing CoC',
  MISSING_CUSTOMER_REPORT: 'Missing customer report PDF',
  SEND_FAILED: 'Send failed',
  JOB_NOT_FOUND: 'Job not found in the database'
};
interface SendCustomerReportNightlyModalProps {
  isOpen: boolean;
  onClose(): void;
}
export const SendCustomerReportNightlyModal: React.FC<SendCustomerReportNightlyModalProps> = t0 => {
  const $ = _c(42);
  const {
    isOpen,
    onClose
  } = t0;
  const isProduction = process.env.APP_ENV === "production";
  const [date, setDate] = useState(_temp);
  const [shipments, setShipments] = useState(null);
  const [preview, setPreview] = useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Set();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [selectedJobIds, setSelectedJobIds] = useState(t1);
  const [sendResults, setSendResults] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const {
    isLoading,
    doServerAction
  } = useServerHandler();
  let t2;
  if ($[1] !== date || $[2] !== doServerAction) {
    t2 = () => {
      doServerAction(async () => {
        const dateString = formatDateAsLocalDateString(date);
        const previewData = await previewCustomerReportNightlyByDate(dateString);
        return {
          data: previewData.shipments
        };
      }, result => {
        if (!result || typeof result !== "object" || !("data" in result)) {
          return;
        }
        setShipments(result.data);
        setPreview(null);
        setSendResults(null);
      });
    };
    $[1] = date;
    $[2] = doServerAction;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const fetchShipments = t2;
  let t3;
  if ($[4] !== date || $[5] !== doServerAction) {
    t3 = () => {
      doServerAction(async () => {
        const dateString_0 = formatDateAsLocalDateString(date);
        return {
          data: await previewCustomerReportNightlyByDate(dateString_0)
        };
      }, result_0 => {
        if (!result_0 || typeof result_0 !== "object" || !("data" in result_0)) {
          return;
        }
        setPreview(result_0.data);
        setSelectedJobIds(new Set(result_0.data.jobsFound.map(_temp2).filter(Boolean)));
        setTabValue(0);
        setSendResults(null);
        setShipments(null);
      });
    };
    $[4] = date;
    $[5] = doServerAction;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const refresh = t3;
  let t4;
  let t5;
  if ($[7] !== isOpen) {
    t4 = () => {
      if (!isOpen) {
        return;
      }
      setShipments(null);
      setPreview(null);
      setSendResults(null);
      setTabValue(0);
    };
    t5 = [isOpen];
    $[7] = isOpen;
    $[8] = t4;
    $[9] = t5;
  } else {
    t4 = $[8];
    t5 = $[9];
  }
  useEffect(t4, t5);
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = d => {
      if (d) {
        setDate(d.toDate());
      }
    };
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const handleDateChange = t6;
  let t7;
  if ($[11] !== isLoading || $[12] !== onClose) {
    t7 = () => {
      if (isLoading) {
        return;
      }
      setShipments(null);
      setPreview(null);
      setSelectedJobIds(new Set());
      setSendResults(null);
      setTabValue(0);
      onClose();
    };
    $[11] = isLoading;
    $[12] = onClose;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const handleClose = t7;
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = jobId => {
      setSelectedJobIds(prev => {
        const next = new Set(prev);
        if (next.has(jobId)) {
          next.delete(jobId);
        } else {
          next.add(jobId);
        }
        return next;
      });
    };
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const toggleJob = t8;
  let t9;
  if ($[15] !== date || $[16] !== doServerAction || $[17] !== preview || $[18] !== selectedJobIds) {
    t9 = () => {
      if (!preview) {
        return;
      }
      const targets = preview.jobsFound.filter(job => selectedJobIds.has(job.jobId as number)).map(_temp3);
      doServerAction(async () => {
        const dateString_1 = formatDateAsLocalDateString(date);
        const sendRes = await runCustomerReportNightlyByDate(targets);
        const summaryResult = await sendCustomerReportSummaryByDate(preview, dateString_1);
        return {
          data: sendRes,
          summary: summaryResult
        };
      }, result_1 => {
        if (!result_1 || typeof result_1 !== "object" || !("data" in result_1)) {
          return;
        }
        setSendResults(result_1.data as CustomerReportSendResult[]);
      });
    };
    $[15] = date;
    $[16] = doServerAction;
    $[17] = preview;
    $[18] = selectedJobIds;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const handleSend = t9;
  const renderIssueChips = _temp5;
  let t10;
  if ($[20] !== fetchShipments || $[21] !== handleClose || $[22] !== handleSend || $[23] !== isLoading || $[24] !== preview || $[25] !== refresh || $[26] !== selectedJobIds || $[27] !== sendResults) {
    t10 = sendResults ? <Button variant="contained" onClick={handleClose}>Close</Button> : <><Button disabled={isLoading} onClick={fetchShipments} variant="outlined">Fetch Shipments</Button><Button disabled={isLoading} onClick={refresh} variant="outlined">Refresh</Button>{!isProduction ? <Button variant="contained" disabled={isLoading || !preview || selectedJobIds.size === 0} onClick={handleSend}>Send {selectedJobIds.size > 0 ? `(${selectedJobIds.size})` : ""}</Button> : null}</>;
    $[20] = fetchShipments;
    $[21] = handleClose;
    $[22] = handleSend;
    $[23] = isLoading;
    $[24] = preview;
    $[25] = refresh;
    $[26] = selectedJobIds;
    $[27] = sendResults;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== date || $[30] !== preview || $[31] !== selectedJobIds || $[32] !== sendResults || $[33] !== shipments || $[34] !== tabValue) {
    t11 = sendResults ? <Box sx={{
      display: "flex",
      flexDirection: "column",
      gap: 2
    }}><Alert severity={sendResults.every(_temp6) ? "success" : "warning"}>{sendResults.filter(_temp7).length} of {sendResults.length} emails sent successfully.</Alert><Table size="small"><TableHead><TableRow><TableCell>Job Number</TableCell><TableCell>Result</TableCell></TableRow></TableHead><TableBody>{sendResults.map(_temp8)}</TableBody></Table></Box> : shipments && !preview ? <Box sx={{
      display: "flex",
      flexDirection: "column",
      gap: 2
    }}><Alert severity="info">Found <strong>{shipments.length}</strong> shipment{shipments.length !== 1 ? "s" : ""} on {formatDateForDisplay(date)}. Manually create jobs in the app for these shipments, then click "Refresh" to see them.</Alert><Table size="small"><TableHead><TableRow><TableCell>Job Number</TableCell><TableCell align="right">Qty</TableCell><TableCell>Tracking Number</TableCell><TableCell>Ship Date</TableCell></TableRow></TableHead><TableBody>{shipments.map(_temp9)}</TableBody></Table></Box> : <Box sx={{
      display: "flex",
      flexDirection: "column",
      gap: 2
    }}><Box sx={{
        maxWidth: 300
      }}><DatePicker label="Select Date" value={date} onChange={handleDateChange} /></Box>{!preview ? <Alert severity="info">Select a date and click <strong>"Fetch Shipments"</strong> to see available shipments. Then manually create jobs in the app and click <strong>"Refresh"</strong> to see them with their status.</Alert> : preview.shipments.length === 0 ? <Alert severity="info">No shipments found for {formatDateForDisplay(date)}.</Alert> : <><Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}><Tab label={`Shipments (${preview.shipments.length})`} /><Tab label={`Jobs Found (${preview.jobsFound.length})`} /><Tab label={`Jobs Not Found (${preview.jobsNotFound.length})`} /><Tab label="Summary" /></Tabs>{tabValue === 0 && <Box><Typography variant="body2" color="text.secondary" sx={{
            mb: 2
          }}>{preview.shipments.length} shipment{preview.shipments.length === 1 ? "" : "s"} for{" "}{preview.summary.totalShipments === 1 ? "1 unique job" : `${preview.summary.totalShipments} unique jobs`} on{" "}{formatDateForDisplay(date)}.</Typography><Table size="small"><TableHead><TableRow><TableCell>Job Number</TableCell><TableCell align="right">Qty</TableCell><TableCell>Tracking Number</TableCell><TableCell>Ship Date</TableCell></TableRow></TableHead><TableBody>{preview.shipments.map(_temp10)}</TableBody></Table></Box>}{tabValue === 1 && <Box><Typography variant="body2" color="text.secondary" sx={{
            mb: 2
          }}>{preview.jobsFound.length} job{preview.jobsFound.length === 1 ? "" : "s"} found in database;{" "}<strong>{preview.summary.havingIssues}</strong> have issues, <strong>{preview.summary.clean}</strong> are clean. Uncheck any you don't want to send.</Typography><Table size="small"><TableHead><TableRow><TableCell padding="checkbox" /><TableCell>Job Number</TableCell><TableCell>Site</TableCell><TableCell>Customer</TableCell><TableCell>Workflow Status</TableCell><TableCell>Has Sheets</TableCell><TableCell>Issues</TableCell></TableRow></TableHead><TableBody>{preview.jobsFound.map(job_1 => <TableRow key={job_1.jobId} hover={true} onClick={() => toggleJob(job_1.jobId as number)} sx={{
                cursor: "pointer"
              }}><TableCell padding="checkbox"><Checkbox checked={selectedJobIds.has(job_1.jobId as number)} onChange={() => toggleJob(job_1.jobId as number)} onClick={_temp11} /></TableCell><TableCell>{job_1.jobNumber}</TableCell><TableCell>{job_1.siteName}</TableCell><TableCell>{job_1.customerName}</TableCell><TableCell>{job_1.jobId ? "Processing" : "Unknown"}</TableCell><TableCell>{(job_1 as CustomerReportPreviewWithShipments['jobsFound'][number]).sheetId ? "Yes" : "No"}</TableCell><TableCell>{renderIssueChips(job_1.reasons)}</TableCell></TableRow>)}</TableBody></Table></Box>}{tabValue === 2 && <Box><Typography variant="body2" color="text.secondary" sx={{
            mb: 2
          }}>{preview.jobsNotFound.length} shipment{preview.jobsNotFound.length === 1 ? "" : "s"} have no matching job in the database. (Info only — cannot be sent.)</Typography>{preview.jobsNotFound.length === 0 ? <Alert severity="success">All shipped jobs are in the database!</Alert> : <Table size="small"><TableHead><TableRow><TableCell>Job Number</TableCell><TableCell align="right">Qty</TableCell><TableCell>Tracking Number</TableCell><TableCell>Ship Date</TableCell></TableRow></TableHead><TableBody>{preview.jobsNotFound.map(_temp12)}</TableBody></Table>}</Box>}{tabValue === 3 && <Stack gap={2}><Box sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: 2
          }}><Box sx={{
              p: 2,
              bgcolor: "background.paper",
              borderRadius: 1,
              border: "1px solid divider"
            }}><Typography variant="caption" color="text.secondary">Total Processed</Typography><Typography variant="h5">{preview.summary.totalShipments}</Typography></Box><Box sx={{
              p: 2,
              bgcolor: "background.paper",
              borderRadius: 1,
              border: "1px solid divider"
            }}><Typography variant="caption" color="text.secondary">Found in DB</Typography><Typography variant="h5">{preview.summary.foundInDB}</Typography></Box><Box sx={{
              p: 2,
              bgcolor: "background.paper",
              borderRadius: 1,
              border: "1px solid divider"
            }}><Typography variant="caption" color="text.secondary">Not Found</Typography><Typography variant="h5">{preview.summary.notFound}</Typography></Box><Box sx={{
              p: 2,
              bgcolor: "background.paper",
              borderRadius: 1,
              border: "1px solid divider"
            }}><Typography variant="caption" color="text.secondary">Having Issues</Typography><Typography variant="h5">{preview.summary.havingIssues}</Typography></Box><Box sx={{
              p: 2,
              bgcolor: "success.light",
              borderRadius: 1,
              border: "1px solid success.main"
            }}><Typography variant="caption" color="success.main">Ready to Send (Clean)</Typography><Typography variant="h5" color="success.main">{preview.summary.clean}</Typography></Box></Box><Alert severity="info">You can send up to <strong>{preview.jobsFound.length}</strong> jobs ({preview.summary.foundInDB} found in DB). Jobs with issues can still be sent, but will have failures logged.</Alert></Stack>}</>}</Box>;
    $[29] = date;
    $[30] = preview;
    $[31] = selectedJobIds;
    $[32] = sendResults;
    $[33] = shipments;
    $[34] = tabValue;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== handleClose || $[37] !== isLoading || $[38] !== isOpen || $[39] !== t10 || $[40] !== t11) {
    t12 = <Modal isOpen={isOpen} onClose={handleClose} title="Send Customer Report Nightly (Test Run)" subTitle="Select a date to preview shipments and their job status before sending." maxWidth="lg" fullWidth={true} disableClose={isLoading} isLoading={isLoading} actions={t10}>{t11}</Modal>;
    $[36] = handleClose;
    $[37] = isLoading;
    $[38] = isOpen;
    $[39] = t10;
    $[40] = t11;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  return t12;
};
function _temp() {
  return new Date(Date.now() - 86400000);
}
function _temp2(j) {
  return j.jobId as number;
}
function _temp3(job_0) {
  return {
    jobId: job_0.jobId as number,
    sheetId: (job_0 as CustomerReportPreviewWithShipments['jobsFound'][number]).sheetId,
    jobNumber: job_0.jobNumber
  };
}
function _temp4(reason) {
  return <Chip key={reason} label={CUSTOMER_REPORT_ISSUE_LABELS[reason]} color={reason === "SEND_FAILED" ? "error" : "warning"} size="small" />;
}
function _temp5(reasons) {
  if (reasons.length === 0) {
    return <Chip label={"\u2713 No issues"} color="success" size="small" />;
  }
  return <Stack direction="row" gap={0.5} flexWrap="wrap">{reasons.map(_temp4)}</Stack>;
}
function _temp6(r) {
  return r.success;
}
function _temp7(r_0) {
  return r_0.success;
}
function _temp8(result_2) {
  return <TableRow key={result_2.jobId}><TableCell>{result_2.jobNumber}</TableCell><TableCell>{result_2.success ? <Chip icon={<CheckCircleOutlineIcon />} label="Sent" color="success" size="small" /> : <Chip icon={<ErrorOutlineIcon />} label="Failed" color="error" size="small" />}</TableCell></TableRow>;
}
function _temp9(shipment, idx) {
  return <TableRow key={idx}><TableCell>{shipment.jobNumber}</TableCell><TableCell align="right">{shipment.qtyJob}</TableCell><TableCell>{shipment.trackingNumber}</TableCell><TableCell>{formatDateForDisplay(shipment.shipDate)}</TableCell></TableRow>;
}
function _temp10(shipment_0, idx_0) {
  return <TableRow key={idx_0}><TableCell>{shipment_0.jobNumber}</TableCell><TableCell align="right">{shipment_0.qtyJob}</TableCell><TableCell>{shipment_0.trackingNumber}</TableCell><TableCell>{formatDateForDisplay(shipment_0.shipDate)}</TableCell></TableRow>;
}
function _temp11(e_0) {
  return e_0.stopPropagation();
}
function _temp12(shipment_1, idx_1) {
  return <TableRow key={idx_1}><TableCell>{shipment_1.jobNumber}</TableCell><TableCell align="right">{shipment_1.qtyJob}</TableCell><TableCell>{shipment_1.trackingNumber}</TableCell><TableCell>{formatDateForDisplay(shipment_1.shipDate)}</TableCell></TableRow>;
}